/**
 * The app's one convention for required fields: a RED asterisk BEFORE the label ("* First name")
 * means the field is REQUIRED — and is actually enforced — and no asterisk means it is optional.
 * Put this inside the field's <Label>, as its FIRST child, ahead of the label text.
 *
 * Before the label, in red, because that is what the people who use hiring software all day are
 * used to reading (it is how their existing applicant-tracking forms mark a field). Every required
 * field in the app goes through this component — there are no literal "Label *" strings left — so
 * the convention can only change here.
 *
 * The asterisk is hidden from assistive tech (it would be read as "star"); screen readers get
 * "(required)". Pair the input with `required` / `aria-required`. The red is --rr-error-fg, the
 * text-on-surface red that passes AA in both themes (the brand colour is not red).
 *
 * Spacing: ui/label is a flex row with gap-2, which would push the label text 8px away from the
 * mark; inside a flex parent the mark pulls the text back so it sits a normal word-space from the
 * label either way (`block` labels and plain <legend>/<h3> parents are not flex).
 */
export default function RequiredMark() {
  return (
    <>
      <span aria-hidden data-required-mark className="mr-1 font-semibold text-[var(--rr-error-fg)] [.flex>&]:-mr-1">
        *
      </span>
      <span className="sr-only">(required) </span>
    </>
  );
}

/** One line shown once at the top of a long form that uses the convention. */
export function RequiredLegend({ className = '', children }: { className?: string; children?: React.ReactNode }) {
  return (
    <p className={`text-sm text-[var(--text-muted)] ${className}`} data-testid="required-legend">
      <span aria-hidden className="font-semibold text-[var(--rr-error-fg)]">
        *
      </span>{' '}
      {children ?? 'Required'}
    </p>
  );
}
